import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Box from '@amzn/meridian/box';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Icon from '@amzn/meridian/icon';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  resetJobConfigTableFilter,
  setCurrentPage,
  setTotalPages,
} from 'src/store/jobConfigPageSlice/jobConfigPageSlice';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';
import { useAppSelector } from 'src/store/store';
import { JobConfigTableDataRow } from 'src/store/jobConfigPageSlice/types';
import ApplicableSitesPopover from '../applicableSitesPopover/applicableSitesPopover';
import UnstyledLink from 'src/components/shared/unstyledLink/unstyledLink';

type TOnSortArgs = {
  sortColumn: keyof JobConfigTableDataRow;
  sortDirection: TableSortDirection;
};

const JobTable = () => {
  const dispatch = useDispatch();

  // Job Config Redux
  const { filteredTableData, searchField, currentPage, perPage } = useAppSelector((state) => state.jobConfigPage);

  // Local state
  const [sortColumn, setSortColumn] = useState<keyof JobConfigTableDataRow>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const [sortedTableData, setSortedTableData] = useState(filteredTableData);

  useEffect(() => {
    return () => {
      dispatch(resetJobConfigTableFilter());
    };
  }, [dispatch]);

  const onSort = useCallback(({ sortColumn, sortDirection }: TOnSortArgs) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  // Filter, sorting, and pagination
  useEffect(() => {
    // filter, sorting, and pagination
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      filteredTableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      [],
      perPage,
    );
    setSortedTableData(paginatedData);
    dispatch(setTotalPages(totalPages));
  }, [sortColumn, sortDirection, filteredTableData, searchField, currentPage, perPage, dispatch]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection, perPage, dispatch]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="job-config__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="name">Job Name</TableCell>
          <TableCell sortColumn="region">Region</TableCell>
          <TableCell sortColumn="jobType">Type</TableCell>
          <TableCell sortColumn="siteType">Site Type</TableCell>
          <TableCell>Applicable Site(s)</TableCell>
          <TableCell sortColumn="active">Is Active?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.id} data-testid="job-config-table__row">
            <TableCell>{row.name || ''}</TableCell>
            <TableCell>{row.region || ''}</TableCell>
            <TableCell>{row.jobType || ''}</TableCell>
            <TableCell>{row.siteType || ''}</TableCell>
            <TableCell>{row.id && <ApplicableSitesPopover jobId={row.id} />}</TableCell>
            <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
            <TableCell>{row.description || ''}</TableCell>
            <TableCell>
              <UnstyledLink to={`/createNewJob/${row.id}`}>
                <Icon tokens={editTokens}></Icon>
              </UnstyledLink>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default JobTable;
